/**
 * The brand ramp, in TypeScript — the one place a marketing surface reads a
 * brand colour from when CSS cannot supply it.
 *
 * `bedrock.css` owns the ramp for anything painted in CSS. But four kinds of
 * surface here paint brand colour from JS and cannot use a token:
 *
 * - **SVG gradient stops** (`Story.tsx`), where `stopColor` takes a colour, not
 *   a `var()` a stop can resolve;
 * - **the showcase widget theme** (`StudioWidget.tsx`), whose `p` is parsed
 *   numerically by `readable-ink.ts` to derive a legible ink — a `var()` would
 *   fail to parse and silently produce no correction;
 * - **scene accent data** (`story-data.ts`, `chat-script.ts`, `widget-parts.tsx`),
 *   which is authored as plain data and interpolated into inline styles;
 * - **the favicon builder** (`seo/content/build-favicons.ts`), which rasterizes
 *   the gradient itself, pixel by pixel, with no browser in the loop.
 *
 * Before this module those five files each carried their own transcription of
 * the ramp — the favicon builder carried TWO, an `Rgb` array and a hand-written
 * SVG string listing the same six stops again — and nothing tied any of them to
 * `bedrock.css` or to the logo they are all reproducing. A retune of the brand
 * would have moved some and left others, which is the failure that looks like a
 * rendering bug rather than a stale constant.
 *
 * `brand-ramp.test.ts` pins all three layers together: these constants against
 * the shipped `bedrock.css` tokens, and against `packages/ui`'s
 * `logo-paths.ts`, which is the product-wide source both are derived from. The
 * test reads both files rather than importing them, so marketing takes no
 * runtime dependency on a module outside its own barrel — and a brand retune
 * upstream fails here loudly instead of drifting.
 */

/**
 * The four NAMED ramp stops — the ones `bedrock.css` also declares as
 * `--bd-cyan` / `--bd-blue` / `--bd-purple` / `--bd-violet`, and the only ones
 * a marketing surface picks an accent from.
 *
 * Uppercase because that is how `packages/ui` declares them; the CSS side is
 * lowercase and the test compares case-insensitively, so neither file has to
 * change to match the other's convention.
 */
export const BRAND = {
  cyan: '#16D2FF',
  blue: '#2B62FF',
  purple: '#7A18E8',
  violet: '#B511E0',
} as const;

/** The named stops in ramp order — for anything painting a gradient stop-wise. */
export const BRAND_RAMP = [BRAND.cyan, BRAND.blue, BRAND.purple, BRAND.violet] as const;

/**
 * The COMPLETE logo gradient — six stops with their offsets, exactly as the
 * mark declares them.
 *
 * Distinct from `BRAND_RAMP` on purpose: the four named stops are a palette to
 * accent with, while this is a reproduction of the mark itself and needs the
 * two intermediate stops (#1AA0FF, #4326EE) that give the ramp its curve. Only
 * the favicon builder reproduces the mark, and dropping those two would render
 * a visibly straighter gradient than the logo beside it.
 */
export const LOGO_GRADIENT_STOPS = [
  { offset: 0, color: '#16D2FF' },
  { offset: 0.27, color: '#1AA0FF' },
  { offset: 0.5, color: '#2B62FF' },
  { offset: 0.72, color: '#4326EE' },
  { offset: 0.9, color: '#7A18E8' },
  { offset: 1, color: '#B511E0' },
] as const;

/** `#rrggbb` → `{ r, g, b }`. Throws on a malformed value rather than guessing. */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) throw new Error(`not a 6-digit hex colour: ${hex}`);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}
